import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowPathIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/solid'
import {
  ClipboardDocumentIcon,
  EllipsisHorizontalIcon,
  FaceSmileIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useUsersApi } from '../services/usersApi'
import {
  useChatApi,
  type ChatMessage,
  type ChatMessageStatus,
  type ChatThreadKind,
} from '../services/chatApi'
import { PICKER_EMOJIS, quickBarEmojis, rememberEmoji } from '../utils/chatEmojis'

export type { ChatMessage as ChatWireframeMessage, ChatMessageStatus }

type LocalMessage = ChatMessage & {
  clientId?: string
  status?: ChatMessageStatus
  pendingBody?: string
}

type ChatPanelProps = {
  kind: ChatThreadKind
  entityId?: string
  title: string
  subtitle: string
  placeholder?: string
  locked?: boolean
  lockedMessage?: string
  lockedActionLabel?: string
  onLockedAction?: () => void
  className?: string
  compact?: boolean
  embedded?: boolean
  pollIntervalMs?: number
  muted?: boolean
  onMuteChange?: (muted: boolean) => void
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('')
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch {
    return ''
  }
}

function linkify(text: string) {
  const parts = text.split(/(https?:\/\/[^\s<]+|@[A-Za-z][A-Za-z0-9.'-]*(?:\s+[A-Za-z][A-Za-z0-9.']*)?)/g)
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline break-all">
          {part}
        </a>
      )
    }
    if (part.startsWith('@') && part.length > 1) {
      return (
        <span key={i} className="font-semibold text-primary-700 dark:text-primary-300">
          {part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

function Avatar({ name, src, mine }: { name: string; src?: string | null; mine?: boolean }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return (
      <img
        src={src}
        alt={name}
        className="flex-shrink-0 w-9 h-9 rounded-full object-cover"
        onError={() => setBroken(true)}
      />
    )
  }
  return (
    <div
      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${
        mine
          ? 'bg-primary-600 text-white'
          : 'bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-100'
      }`}
    >
      {initials(name)}
    </div>
  )
}

function mergeMessages(server: ChatMessage[], localPending: LocalMessage[]): LocalMessage[] {
  const byId = new Map<string, LocalMessage>()
  for (const msg of server) {
    byId.set(msg.id, { ...msg, status: msg.status || 'sent' })
  }
  for (const pending of localPending) {
    if (pending.status === 'sending' || pending.status === 'failed') {
      byId.set(pending.clientId || pending.id, pending)
    }
  }
  return [...byId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

function EmojiPickerPopover({
  onPick,
  onClose,
}: {
  onPick: (emoji: string) => void
  onClose: () => void
}) {
  const [showAll, setShowAll] = useState(false)
  const quick = quickBarEmojis()
  const list = showAll ? PICKER_EMOJIS : quick

  return (
    <div className="absolute z-30 bottom-full mb-2 left-0 right-0 sm:right-auto sm:w-72 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl p-2">
      <div className="flex items-center justify-between px-1 pb-2">
        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
          {showAll ? 'All reactions' : 'Quick reactions'}
        </p>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
      <div className={`grid gap-1 ${showAll ? 'grid-cols-8 max-h-48 overflow-y-auto' : 'grid-cols-6'}`}>
        {list.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => {
              rememberEmoji(emoji)
              onPick(emoji)
            }}
            className="h-9 w-full rounded-lg text-xl hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {emoji}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        className="mt-2 w-full text-xs font-medium text-primary-700 dark:text-primary-300 py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30"
      >
        {showAll ? 'Show fewer' : 'More emojis…'}
      </button>
    </div>
  )
}

export default function ChatPanel({
  kind,
  entityId,
  title,
  subtitle,
  placeholder = 'Write a message…',
  locked = false,
  lockedMessage,
  lockedActionLabel,
  onLockedAction,
  className = '',
  compact = false,
  embedded = false,
  pollIntervalMs = 4000,
  muted = false,
  onMuteChange,
}: ChatPanelProps) {
  const { user } = useAuth0()
  const chatApi = useChatApi()
  const usersApi = useUsersApi()
  const [serverMessages, setServerMessages] = useState<ChatMessage[]>([])
  const [pending, setPending] = useState<LocalMessage[]>([])
  const [draft, setDraft] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [apiLocked, setApiLocked] = useState(false)
  const [apiLockedMessage, setApiLockedMessage] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [reactFor, setReactFor] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [muteBusy, setMuteBusy] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const meName = useMemo(() => user?.name || user?.email || 'You', [user])

  const { data: profile } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => usersApi.getCurrentUser(),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  })
  const myAvatar = profile?.avatar || user?.picture || undefined
  const isLocked = locked || apiLocked
  const lockedText = apiLockedMessage || lockedMessage || 'You do not have access to this chat yet.'
  const messages = useMemo(() => mergeMessages(serverMessages, pending), [serverMessages, pending])

  const upsertServerMessage = (msg: ChatMessage) => {
    setServerMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msg.id)
      if (idx === -1) return [...prev, msg]
      const next = [...prev]
      next[idx] = msg
      return next
    })
  }

  const loadMessages = async (opts?: { silent?: boolean }) => {
    if (locked) {
      setLoading(false)
      return
    }
    try {
      if (!opts?.silent) setLoading(true)
      const data = await chatApi.listMessages(kind, entityId, { limit: 80 })
      setServerMessages(data.messages || [])
      setLoadError(null)
      setApiLocked(false)
      setApiLockedMessage(undefined)
      setPending((prev) =>
        prev.filter((p) => {
          if (p.status === 'failed') return true
          if (p.status !== 'sending') return false
          return !(data.messages || []).some(
            (m) => m.body === p.body && m.userId === (user?.sub || p.userId)
          )
        })
      )
    } catch (err: any) {
      if (err?.locked) {
        setApiLocked(true)
        setApiLockedMessage(err.lockedMessage)
      } else if (!opts?.silent) {
        setLoadError(err?.message || 'Failed to load messages')
      }
    } finally {
      if (!opts?.silent) setLoading(false)
    }
  }

  useEffect(() => {
    setServerMessages([])
    setPending([])
    setReplyTo(null)
    setEditingId(null)
    setLoadError(null)
    setApiLocked(false)
    loadMessages()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, entityId, locked])

  useEffect(() => {
    if (isLocked) return
    const id = window.setInterval(() => loadMessages({ silent: true }), pollIntervalMs)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, entityId, isLocked, pollIntervalMs])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const deliver = async (clientId: string, body: string, replyToMessageId?: string) => {
    try {
      const saved = await chatApi.sendMessage(kind, body, entityId, replyToMessageId)
      setPending((prev) => prev.filter((p) => p.clientId !== clientId))
      upsertServerMessage({ ...saved, status: 'sent' })
    } catch {
      setPending((prev) =>
        prev.map((p) => (p.clientId === clientId ? { ...p, status: 'failed' as const } : p))
      )
    }
  }

  const send = () => {
    const body = draft.trim()
    if (!body || isLocked || editingId) return
    const clientId = `local-${Date.now()}`
    const optimistic: LocalMessage = {
      id: clientId,
      clientId,
      userId: user?.sub || 'me',
      authorName: meName,
      authorAvatar: myAvatar,
      body,
      createdAt: new Date().toISOString(),
      isMine: true,
      status: 'sending',
      pendingBody: body,
      replyTo: replyTo
        ? {
            messageId: replyTo.id,
            authorName: replyTo.authorName,
            body: replyTo.body.slice(0, 120),
          }
        : null,
    }
    const replyId = replyTo?.id
    setPending((prev) => [...prev, optimistic])
    setDraft('')
    setReplyTo(null)
    void deliver(clientId, body, replyId)
  }

  const resend = (clientId: string) => {
    const target = pending.find((p) => p.clientId === clientId)
    if (!target || target.status !== 'failed') return
    const body = target.pendingBody || target.body
    setPending((prev) =>
      prev.map((p) => (p.clientId === clientId ? { ...p, status: 'sending' } : p))
    )
    void deliver(clientId, body, target.replyTo?.messageId)
  }

  const saveEdit = async (messageId: string) => {
    const body = editDraft.trim()
    if (!body) return
    try {
      const updated = await chatApi.editMessage(kind, messageId, body, entityId)
      upsertServerMessage(updated)
      setEditingId(null)
      setEditDraft('')
    } catch (err: any) {
      window.alert(err?.message || 'Could not edit message')
    }
  }

  const removeMessage = async (messageId: string) => {
    if (!window.confirm('Delete this message?')) return
    try {
      const updated = await chatApi.deleteMessage(kind, messageId, entityId)
      upsertServerMessage(updated)
      setMenuFor(null)
    } catch (err: any) {
      window.alert(err?.message || 'Could not delete message')
    }
  }

  const react = async (messageId: string, emoji: string) => {
    setReactFor(null)
    try {
      const updated = await chatApi.toggleReaction(kind, messageId, emoji, entityId)
      upsertServerMessage(updated)
    } catch (err: any) {
      window.alert(err?.message || 'Could not add reaction')
    }
  }

  const copyMessage = async (body: string) => {
    try {
      await navigator.clipboard.writeText(body)
      setMenuFor(null)
    } catch {
      window.alert('Could not copy')
    }
  }

  const toggleMute = async () => {
    if (!onMuteChange) return
    setMuteBusy(true)
    try {
      const result = await chatApi.setMute(kind, !muted, entityId)
      onMuteChange(Boolean(result.muted))
    } catch (err: any) {
      window.alert(err?.message || 'Could not update mute')
    } finally {
      setMuteBusy(false)
    }
  }

  const isMine = (msg: LocalMessage) => msg.isMine || (!!user?.sub && msg.userId === user.sub)

  return (
    <div
      className={`flex flex-col bg-white dark:bg-gray-800 overflow-hidden ${
        embedded
          ? 'h-full border-0 rounded-none shadow-none'
          : 'rounded-xl border border-gray-200 dark:border-gray-700 shadow-md'
      } ${className}`}
    >
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{title}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
        </div>
        {onMuteChange && !isLocked && (
          <button
            type="button"
            disabled={muteBusy}
            onClick={toggleMute}
            className="flex-shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {muted ? 'Unmute' : 'Mute'}
          </button>
        )}
      </div>

      {isLocked ? (
        <div className={`flex flex-col items-center justify-center text-center px-6 ${compact ? 'py-12' : 'py-20'}`}>
          <p className="text-gray-700 dark:text-gray-200 max-w-md">{lockedText}</p>
          {lockedActionLabel && onLockedAction && (
            <button
              type="button"
              onClick={onLockedAction}
              className="mt-4 inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            >
              {lockedActionLabel}
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            className={`flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gray-50/80 dark:bg-gray-900/40 ${
              compact
                ? 'min-h-[280px] max-h-[420px]'
                : embedded
                  ? 'min-h-0'
                  : 'min-h-[420px] max-h-[60vh]'
            }`}
            onClick={() => {
              setMenuFor(null)
              setReactFor(null)
            }}
          >
            {loading && messages.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading…</p>
            )}
            {loadError && (
              <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300 flex items-center justify-between gap-2">
                <span>{loadError}</span>
                <button type="button" onClick={() => loadMessages()} className="font-semibold underline">
                  Retry
                </button>
              </div>
            )}
            {!loading && !loadError && messages.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No messages yet — say hello.
              </p>
            )}

            {messages.map((msg) => {
              const mine = isMine(msg)
              const avatarSrc = mine ? myAvatar || msg.authorAvatar : msg.authorAvatar
              const failed = msg.status === 'failed'
              const sending = msg.status === 'sending'
              const deleted = Boolean(msg.deletedAt)
              const msgKey = msg.clientId || msg.id

              return (
                <div
                  key={msgKey}
                  className={`flex gap-3 ${mine ? 'flex-row-reverse' : 'flex-row'} group relative`}
                >
                  <Avatar name={mine ? meName : msg.authorName} src={avatarSrc} mine={mine} />
                  <div className={`max-w-[80%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                    <div className={`flex items-baseline gap-2 mb-1 ${mine ? 'flex-row-reverse' : ''}`}>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {mine ? 'You' : msg.authorName}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(msg.createdAt)}
                      </span>
                      {msg.editedAt && !deleted && (
                        <span className="text-[10px] text-gray-400 italic">Edited</span>
                      )}
                    </div>

                    {msg.replyTo && !deleted && (
                      <div
                        className={`mb-1 max-w-full rounded-lg px-2.5 py-1.5 text-xs border-l-2 ${
                          mine
                            ? 'bg-primary-700/40 border-primary-200 text-primary-50'
                            : 'bg-gray-200/70 dark:bg-gray-700/70 border-primary-500 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <p className="font-semibold truncate">{msg.replyTo.authorName}</p>
                        <p className="truncate opacity-90">{msg.replyTo.body}</p>
                      </div>
                    )}

                    {editingId === msg.id ? (
                      <div className="w-full space-y-2">
                        <textarea
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          rows={3}
                          className="w-full resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                          autoFocus
                        />
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null)
                              setEditDraft('')
                            }}
                            className="text-xs px-2 py-1 rounded-lg text-gray-600 dark:text-gray-300"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(msg.id)}
                            className="text-xs px-3 py-1 rounded-lg bg-primary-600 text-white"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                          deleted
                            ? 'bg-transparent text-gray-400 italic border border-dashed border-gray-300 dark:border-gray-600'
                            : failed
                              ? mine
                                ? 'bg-primary-600/70 text-white rounded-tr-md ring-2 ring-red-400'
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-red-300 rounded-tl-md'
                              : mine
                                ? `bg-primary-600 text-white rounded-tr-md ${sending ? 'opacity-70' : ''}`
                                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-tl-md'
                        }`}
                      >
                        {deleted ? 'Message deleted' : linkify(msg.body)}
                      </div>
                    )}

                    {!deleted && !sending && !failed && editingId !== msg.id && (
                      <div className={`mt-1 flex flex-wrap items-center gap-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                        {(msg.reactions || []).map((r) => (
                          <button
                            key={r.emoji}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              void react(msg.id, r.emoji)
                            }}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs border ${
                              r.reactedByMe
                                ? 'bg-primary-50 dark:bg-primary-900/40 border-primary-300 dark:border-primary-600'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600'
                            }`}
                          >
                            <span>{r.emoji}</span>
                            <span className="text-gray-600 dark:text-gray-300">{r.count}</span>
                          </button>
                        ))}

                        <div className="relative opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setReactFor(reactFor === msg.id ? null : msg.id)
                              setMenuFor(null)
                            }}
                            className="p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700"
                            aria-label="Add reaction"
                          >
                            <FaceSmileIcon className="w-4 h-4" />
                          </button>
                          {reactFor === msg.id && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <EmojiPickerPopover
                                onPick={(emoji) => void react(msg.id, emoji)}
                                onClose={() => setReactFor(null)}
                              />
                            </div>
                          )}
                        </div>

                        <div className="relative opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setMenuFor(menuFor === msg.id ? null : msg.id)
                              setReactFor(null)
                            }}
                            className="p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700"
                            aria-label="Message options"
                          >
                            <EllipsisHorizontalIcon className="w-4 h-4" />
                          </button>
                          {menuFor === msg.id && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className={`absolute z-20 mt-1 w-40 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1 ${
                                mine ? 'right-0' : 'left-0'
                              }`}
                            >
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                                onClick={() => {
                                  setReplyTo(msg)
                                  setMenuFor(null)
                                }}
                              >
                                Reply
                              </button>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                                onClick={() => void copyMessage(msg.body)}
                              >
                                <ClipboardDocumentIcon className="w-4 h-4" />
                                Copy
                              </button>
                              {msg.canEdit && (
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                                  onClick={() => {
                                    setEditingId(msg.id)
                                    setEditDraft(msg.body)
                                    setMenuFor(null)
                                  }}
                                >
                                  <PencilSquareIcon className="w-4 h-4" />
                                  Edit
                                </button>
                              )}
                              {msg.canDelete && (
                                <button
                                  type="button"
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                                  onClick={() => void removeMessage(msg.id)}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  Delete
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {mine && sending && (
                      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Sending…</p>
                    )}
                    {mine && failed && msg.clientId && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-400">
                          <ExclamationCircleIcon className="h-3.5 w-3.5" />
                          Not Delivered
                        </span>
                        <button
                          type="button"
                          onClick={() => resend(msg.clientId!)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 dark:text-primary-300 hover:underline"
                        >
                          <ArrowPathIcon className="h-3.5 w-3.5" />
                          Resend
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {replyTo && (
              <div className="mb-2 flex items-start justify-between gap-2 rounded-lg bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold text-primary-700 dark:text-primary-300">
                    Replying to {replyTo.authorName}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{replyTo.body}</p>
                </div>
                <button type="button" onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            )}
            <p className="mb-1.5 text-[11px] text-gray-400">Tip: @Name to mention · reactions via 🙂</p>
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                send()
              }}
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    send()
                  }
                }}
                rows={2}
                placeholder={placeholder}
                className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={!draft.trim() || Boolean(editingId)}
                className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Send"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  )
}
